import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AlertCircle, ArrowRight, Check } from 'lucide-react'
import { submitExperience } from '../actions/userActions'
import { SUBMIT_EXPERIENCE_RESET } from '../constants/userConstants'
import SEO from '../components/SEO'
import './Contactscreen.css'

const MAX = 1000
const NEAR = 900
const SUPPORT_EMAIL = 'admin@pcrassist.com'

function Contactscreen() {
  const [text, setText] = useState('')
  const [sent, setSent] = useState(false)

  const dispatch = useDispatch()

  const submitExperienceData = useSelector((state) => state.submitExperience)
  const { loading, error, success } = submitExperienceData

  // Success is consumed once, then the redux flag is reset so re-entering the
  // page doesn't re-open the confirmation.
  useEffect(() => {
    if (!success) return
    setSent(true)
    setText('')
    dispatch({ type: SUBMIT_EXPERIENCE_RESET })
  }, [success, dispatch])

  const canSubmit = text.trim().length > 0 && !loading

  const submitHandler = (e) => {
    e.preventDefault()
    if (!canSubmit) return
    dispatch(submitExperience(text))
  }

  const countState = text.length >= NEAR ? 'near' : 'idle'

  return (
    <>
      <SEO
        title="Contact Us - PCRAssist - PCR Assistant"
        description="Get in touch with our team for support, feedback, or inquiries about PCRAssist AI-powered protocol generation tools."
        canonicalUrl="/contact"
      />

      <div className="ct">
        <div className="ct__wrap">
          {/* ---- head — S2 hanging, no rule ------------------------- */}
          <header className="ct__head">
            <h1 className="ct__title">
              Talk to the <b>team</b>.
            </h1>
            <p className="ct__lede">
              Support questions, feature requests, or how the tool held up on a call. Three ways to
              reach us — take whichever suits you.
            </p>
          </header>

          {/* ---- the index — three real routes ---------------------- */}
          <ul className="ct__routes">
            {/* Route 1 — the form. Expanded because it's the one we want. */}
            <li className="ct__route ct__route--primary">
              <h2 className="ct__route-term">Send us a note</h2>
              <p className="ct__route-note">
                Support, a feature request, or how the last call actually went. The more specific
                you are, the more we can do with it.
              </p>

              {sent ? (
                <div className="ct__sent" role="status">
                  <div className="ct__sent-head">
                    <Check size={18} aria-hidden="true" />
                    <span className="ct__sent-title">Your note is with us</span>
                  </div>
                  <p>
                    Thanks &mdash; it&rsquo;s been submitted. If it needs a reply, you&rsquo;ll
                    hear back within 1&ndash;2 business days.
                  </p>
                  <button type="button" className="ct__btn" onClick={() => setSent(false)}>
                    Write another
                  </button>
                </div>
              ) : (
                <form className="ct__form" onSubmit={submitHandler} noValidate>
                  <div className="ct__surface">
                    <div className="ct__surface-bar">
                      <label className="ct__label" htmlFor="ct-message">
                        Your message
                      </label>
                      <span className="ct__count" data-state={countState}>
                        {text.length}/{MAX}
                      </span>
                    </div>

                    <textarea
                      id="ct-message"
                      className="ct__editor"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      maxLength={MAX}
                      aria-describedby="ct-help"
                      placeholder="Would be useful if the protocol answers linked back to the page they came from…"
                    />

                    <p className="ct__helper" id="ct-help">
                      {countState === 'near'
                        ? `${MAX - text.length} characters left.`
                        : `Up to ${MAX.toLocaleString()} characters.`}
                    </p>
                  </div>

                  {error && (
                    <p className="ct__error" role="alert">
                      <AlertCircle size={16} aria-hidden="true" />
                      <span>
                        {typeof error === 'string' ? error : 'Your note didn’t send.'} Try again, or
                        email us at {SUPPORT_EMAIL}.
                      </span>
                    </p>
                  )}

                  <div className="ct__actions">
                    <button type="submit" className="ct__btn ct__btn--primary" disabled={!canSubmit}>
                      <span className="ct__btn-label">{loading ? 'Sending…' : 'Send message'}</span>
                      {!loading && <ArrowRight size={16} aria-hidden="true" />}
                    </button>
                  </div>
                </form>
              )}
            </li>

            {/* Route 2 — the direct address. */}
            <li className="ct__route ct__route--alt">
              <h2 className="ct__route-term">Email us directly</h2>
              <p className="ct__route-note">
                For anything you&rsquo;d rather send from your own inbox, or with a file attached.
              </p>
              <a className="ct__link" href={`mailto:${SUPPORT_EMAIL}`}>
                {SUPPORT_EMAIL}
              </a>
            </li>

            {/* Route 3 — the pages that already answer it. */}
            <li className="ct__route ct__route--alt">
              <h2 className="ct__route-term">Answer it now</h2>
              <p className="ct__route-note">
                Common questions and a walkthrough of the report flow, if you&rsquo;d rather not
                wait.
              </p>
              <span className="ct__link-row">
                <Link className="ct__link" to="/faq">
                  Read the FAQ
                </Link>
                <Link className="ct__link" to="/howto">
                  How it works
                </Link>
              </span>
            </li>
          </ul>

          <p className="ct__foot">We usually respond within 1&ndash;2 business days.</p>
        </div>
      </div>
    </>
  )
}

export default Contactscreen
